/**
 * Nesting: a banner group of condition columns made from two groups, one
 * column for each column of the first with each column of the second
 * ("Male / 18-34"). The two groups stay as they are. Pure.
 */
import { refusedName } from "../compiler/names"
import { Catalogue, CatalogueInstance, findInstance, findQuestion } from "../types/catalogue"
import {
  BannerGroup,
  BannerPoint,
  Condition,
  ConditionGroup,
  ConditionTerm,
  isConditionGroup,
  isNoneGroup,
  newTabSpecId,
} from "../types/tabSpecDocument"
import { RAW_NAMES, ShownNames } from "./shownLabels"

/** The instance whose paths a group's columns use: the one it names, or its question's only one. */
const instanceOf = (group: BannerGroup, catalogue: Catalogue): CatalogueInstance | null => {
  if (isConditionGroup(group)) return null
  const q = findQuestion(catalogue, group.variable, group.source)
  if (!q) return null
  if (group.instance !== undefined) return findInstance(q, group.instance)
  return q.instances.length === 1 ? q.instances[0] : null
}

const copyTerm = (t: ConditionTerm): ConditionTerm => (typeof t === "string" ? t : { ...t })
const copyGroup = (g: ConditionGroup): ConditionGroup =>
  isNoneGroup(g) ? { none: g.none.map(copyTerm) } : g.map(copyTerm)

/**
 * The condition a column of `group` stands for: a category is its answer
 * (`[[path]]`), a net any of its answers (`[[...paths]]`), a range its
 * number between the bounds (`[[{path, min, max}]]`), a condition its own
 * groups. The paths are those of the group's instance, so a column of a grid
 * row names that row. null when it stands for nothing that can be found: the
 * question, the row or every answer is gone, a range has no bound, a
 * condition no group.
 */
export const pointCondition = (
  group: BannerGroup,
  point: BannerPoint,
  catalogue: Catalogue
): Condition | null => {
  if (point.kind === "condition") return point.condition.length > 0 ? point.condition.map(copyGroup) : null
  const instance = instanceOf(group, catalogue)
  if (!instance) return null
  if (point.kind === "range") {
    if (point.min === undefined && point.max === undefined) return null
    return [
      [
        {
          path: instance.path,
          ...(point.min !== undefined ? { min: point.min } : {}),
          ...(point.max !== undefined ? { max: point.max } : {}),
        },
      ],
    ]
  }
  const members = point.kind === "category" ? [point.label] : point.members
  const paths = members.flatMap((m) => (instance.optionPaths[m] ? [instance.optionPaths[m]] : []))
  return paths.length > 0 ? [paths] : null
}

/** The variable's label the names of a group's answers are looked up under. */
const variableOf = (group: BannerGroup, catalogue: Catalogue | null): string =>
  (catalogue ? findQuestion(catalogue, group.variable, group.source)?.label : undefined) ??
  group.variable

/**
 * The heading of `group` as the tables show it, before numbering: its own,
 * else its question as shown (a variable nested in the data by its parts),
 * with the grid row or loop iteration it names after " - " ("Recommend -
 * Evrysdi"). "" for a group of conditions with no heading.
 */
export const groupWords = (
  group: BannerGroup,
  catalogue: Catalogue | null,
  names: ShownNames = RAW_NAMES
): string => {
  const own = (group.label ?? "").trim()
  if (own || isConditionGroup(group)) return own
  const label = variableOf(group, catalogue)
  const shown = names.variable(label)
  const words = shown === label ? label.split("//").join(": ") : shown
  if (group.instance === undefined) return words
  const q = catalogue ? findQuestion(catalogue, group.variable, group.source) : null
  const row = (q ? findInstance(q, group.instance)?.label : undefined) || group.instance
  return `${words} - ${names.instance(label, group.instance, row)}`
}

/**
 * The columns of `group` as the editor lists them: its points, or every
 * answer of its question while it follows the questionnaire.
 */
const columnsOf = (group: BannerGroup, catalogue: Catalogue): BannerPoint[] => {
  if (group.points) return group.points
  if (isConditionGroup(group)) return []
  const q = findQuestion(catalogue, group.variable, group.source)
  return (q?.options ?? []).map((o) => ({ kind: "category" as const, label: o.label }))
}

/**
 * Whether the engine takes `name` as a column label or a heading: a nested
 * group's names are its engine names, with no shown name of their own.
 */
const engineTakes = (name: string): boolean => refusedName(name, [], "column") === null

/**
 * A column's name in a nested group: as the tables show it (a category's
 * rename or shown label, any other's name) while the engine takes it; else
 * the rename, else the label (a shown label may hold a quote or end with
 * ">", which only a shown name can).
 */
const columnName = (group: BannerGroup, point: BannerPoint, catalogue: Catalogue, names: ShownNames): string => {
  if (point.kind !== "category") return point.name.trim()
  const rename = point.rename?.trim()
  const shown = rename || names.option(variableOf(group, catalogue), point.label)
  if (engineTakes(shown)) return shown
  return rename && engineTakes(rename) ? rename : point.label
}

/**
 * A group's heading in a nested group's heading: as the tables show it
 * (`groupWords`) while the engine takes it, else in its own labels.
 */
const headingOf = (group: BannerGroup, catalogue: Catalogue, names: ShownNames): string => {
  const shown = groupWords(group, catalogue, names)
  return engineTakes(shown) ? shown : groupWords(group, catalogue, RAW_NAMES)
}

/**
 * A new group of conditions: `outer` by `inner`. Its heading is "<outer
 * heading> by <inner heading>"; it has one column per column of `outer` with
 * each column of `inner`, in that order, named "<outer column> / <inner
 * column>" as the tables show them, its condition the outer column's groups
 * then the inner column's. Those names reach the engine: a shown name the
 * engine cannot take gives way to the column's own label, and a heading to
 * its own words. A column that stands for nothing that can be found
 * (`pointCondition`) is left out of the pairs. Both groups stay in the
 * banner as they are; nothing is shared with them.
 */
export const nestGroups = (
  outer: BannerGroup,
  inner: BannerGroup,
  catalogue: Catalogue,
  names: ShownNames = RAW_NAMES,
  makeId: () => string = () => newTabSpecId("group")
): BannerGroup => {
  const sides = [outer, inner].map((g) =>
    columnsOf(g, catalogue).flatMap((p) => {
      const condition = pointCondition(g, p, catalogue)
      return condition ? [{ name: columnName(g, p, catalogue, names), condition }] : []
    })
  )
  const points: BannerPoint[] = sides[0].flatMap((o) =>
    sides[1].map((i) => ({
      kind: "condition" as const,
      name: `${o.name} / ${i.name}`,
      condition: [...o.condition.map(copyGroup), ...i.condition.map(copyGroup)],
    }))
  )
  return {
    id: makeId(),
    variable: "",
    label: `${headingOf(outer, catalogue, names)} by ${headingOf(inner, catalogue, names)}`,
    points,
  }
}
